import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  recordSessionEntryOnce,
  recordPageview,
  updatePageviewMetrics,
  isMarketingPath,
} from "@/lib/marketingTracking";

/**
 * Mount once inside <BrowserRouter>. On every route change it:
 *   - records the session entry once (with UTMs + landing path)
 *   - inserts a pageview row
 *   - tracks dwell time and max scroll depth, flushing them on unmount /
 *     route change / tab hide / page unload
 *
 * The tracker runs on every route (marketing + app) — pageviews on
 * authenticated routes are still useful for funnel analytics. Bots are
 * filtered server-side later.
 */
export function MarketingTracker() {
  const location = useLocation();
  const pageviewIdRef = useRef<string | null>(null);
  const enteredAtRef = useRef<number>(Date.now());
  const maxScrollRef = useRef<number>(0);
  const flushedRef = useRef<boolean>(false);

  // Capture UTMs + first landing exactly once per browser session
  useEffect(() => {
    void recordSessionEntryOnce();
  }, []);

  // Per-route pageview lifecycle (marketing pages only)
  useEffect(() => {
    if (!isMarketingPath(location.pathname)) return;

    flushedRef.current = false;
    enteredAtRef.current = Date.now();
    maxScrollRef.current = 0;
    pageviewIdRef.current = null;


    (async () => {
      const id = await recordPageview(location.pathname, document.referrer || null);
      pageviewIdRef.current = id;
    })();

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = Math.max(1, doc.scrollHeight - doc.clientHeight);
      const pct = Math.min(100, Math.round(((window.scrollY || 0) / scrollable) * 100));
      if (pct > maxScrollRef.current) maxScrollRef.current = pct;
    };

    const flush = () => {
      if (flushedRef.current) return;
      flushedRef.current = true;
      const id = pageviewIdRef.current;
      if (!id) return;
      const dwell = Date.now() - enteredAtRef.current;
      void updatePageviewMetrics(id, dwell, maxScrollRef.current);
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, [location.pathname]);

  return null;
}
